import { DrawerProps, StackProps, Theme, styled } from '@mui/material';
import { FC, Fragment, useState } from 'react';
import {
	Dashboard_Actions,
	Dashboard_ManageAccount,
	Home,
	Logout,
	Notifications,
} from 'utils/links';

import { CSSObject } from '@emotion/react';
import { ReactComponent as ChevronLeftIcon } from 'assett/icons/chevron_left_icon.svg';
import { ReactComponent as ChevronRightIcon } from 'assett/icons/chevron_right_icon.svg';
import useDevice from 'hooks/DesktopDevice.hook';
import { useLogout } from 'hooks/login.hook';
import mobile from 'is-mobile';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from 'store/store';
import { List } from 'styles';
import Box from 'styles/box/Box';
import Divider from 'styles/divider/Divider';
import Drawer from 'styles/drawer/Drawer';
import Stack from 'styles/stack/Stack';
import { useTranslation } from 'utils/translations';
import ListItemButton from './ListItemButton.component';

const DRAWER_WIDTH = '100%'; // MEMO: before was 336 for DESKTOP and 315 for TABLET

const openedMixin = (theme: Theme, desktop: boolean): CSSObject => ({
	width: DRAWER_WIDTH,
	transition: theme.transitions.create('width', {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.enteringScreen,
	}),
	boxShadow: '3px 3px 12px 0px rgba(0, 0, 0, 0.05)',
	overflowX: 'hidden',
});

const closedMixin = (theme: Theme, desktop: boolean): CSSObject => ({
	transition: theme.transitions.create('width', {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen,
	}),
	overflowX: 'hidden',
	boxShadow: '3px 3px 12px 0px rgba(0, 0, 0, 0.05)',
	'&.MuiStack-root>.MuiStack-root>.MuiDivider-root': {
		width: 32,
	},
	width: `calc(${theme.spacing(8.5)} + 1px)`,
	[theme.breakpoints.up('lg')]: {
		width: `calc(${theme.spacing(10)} + 1px)`,
	},
});

type DrawerStyleProps = DrawerProps & {
	desktop: boolean;
};

const StyledDrawer = styled(Drawer, {
	shouldForwardProp: prop => !['open', 'desktop'].includes(String(prop)),
})<DrawerStyleProps>(({ theme, open, desktop }) => ({
	width: DRAWER_WIDTH,
	flexShrink: 0,
	whiteSpace: 'nowrap',
	boxSizing: 'border-box',
	...(open && {
		...openedMixin(theme, desktop),
		'&.MuiDrawer-paper.left-sidebar': openedMixin(theme, desktop),
	}),
	...(!open && {
		...closedMixin(theme, desktop),
		'&.MuiDrawer-paper.left-sidebar': closedMixin(theme, desktop),
	}),
}));

type StackPropsStyled = StackProps & {
	open: boolean;
	desktop: boolean;
};

const StyledButtonSidebar = styled(Stack, {
	shouldForwardProp: prop => !['open', 'desktop'].includes(String(prop)),
})<StackPropsStyled>(({ theme, open, desktop }) => ({
	width: 24,
	height: '100vh',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	flexShrink: 0,
	marginLeft: -1,
	'& .Sidebar-Button': {
		boxShadow: '9px 1px 15px 0px rgba(0, 0, 0, 0.05)',
		borderTopRightRadius: 15,
		borderBottomRightRadius: 15,
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		background: 'white',
		height: 129,
		zIndex: 10000,
		marginLeft: !open && !desktop ? -1 : 0,
		cursor: 'pointer',
	},
}));

const Sidebar: FC = () => {
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: 'clientLabels',
	});
	const [open, setOpen] = useState<boolean>(false);
	const navigate = useNavigate();
	const { isDesktop } = useDevice();
	const handleLogout = useLogout();

	const { features } = useSelector((store: RootState) => store.user);
	const { totalClaims } = useSelector((store: RootState) => store.claims);
	const featuresRole = features.map(el => el.code);

	const isMobile = mobile({ tablet: true, featureDetect: true });

	const [maxPageHeight] = useState(window.innerHeight);

	return (
		<Fragment>
			<Box
				sx={{
					position: 'absolute',
					top: 0,
					left: 0,
					width: open ? '100%' : '0%',
					height: `${window.innerHeight}px`,
					background: 'rgba(50, 50, 50, 0.25)',
					opacity: open ? 0.6 : 0,
					zIndex: 99,
				}}
			/>
			<Box position={'fixed'} left={0} zIndex={100}>
				<Box sx={{ position: 'fixed', display: 'flex' }} role="presentation">
					<StyledDrawer
						desktop={isDesktop}
						variant="permanent"
						open={open}
						PaperProps={{
							className: 'left-sidebar',
							sx: {
								borderRight: 0,
								borderTopRightRadius: 10,
								borderBottomRightRadius: 10,
								position: 'static',
							},
						}}
					>
						<Stack
							height={`${isMobile ? maxPageHeight : window.innerHeight}px`}
							justifyContent={'space-evenly'}
						>
							<Stack flexGrow={1} sx={{ px: !!isDesktop ? 3 : 2.25 }}>
								<List sx={{ rowGap: 5 }}>
									{Notifications(tClientLabels).map(action => (
										<ListItemButton
											key={action.id}
											open={open}
											action={action}
											onClick={() => navigate(action.link)}
											sx={{ mt: 6.5 }}
											notifications={totalClaims}
										/>
									))}
								</List>
							</Stack>
							<Stack
								flexGrow={2}
								sx={{ px: !!isDesktop ? 3 : 2.25, overflow: 'hidden' }}
							>
								<List sx={{ rowGap: 5 }}>
									{Home(tClientLabels).map(action => (
										<ListItemButton
											key={action.id}
											open={open}
											action={action}
											onClick={() => navigate(action.link)}
											sx={{ mt: 6.5 }}
										/>
									))}
								</List>
								<Divider sx={{ my: 3.75 }} />
								<List sx={{ display: 'flex', flexDirection: 'column', rowGap: 5 }}>
									{Dashboard_Actions(tClientLabels)
										.filter(link => link.feature.find(el => featuresRole.includes(el)))
										.map(action => (
											<ListItemButton
												key={action.id}
												open={open}
												action={action}
												onClick={() => navigate(action.link)}
											/>
										))}
								</List>
								<Divider sx={{ my: 3.75 }} />
								<List sx={{ rowGap: 5, display: 'flex', flexDirection: 'column' }}>
									{Dashboard_ManageAccount(tClientLabels)
										.filter(link => link.feature.find(el => featuresRole.includes(el)))
										.map(action => (
											<ListItemButton
												key={action.id}
												open={open}
												action={action}
												onClick={() => navigate(action.link)}
											/>
										))}
								</List>
							</Stack>
							<Stack flexGrow={0} sx={{ px: !!isDesktop ? 3 : 2.25 }}>
								<List>
									{Logout(tClientLabels).map(action => (
										<ListItemButton
											key={action.id}
											open={open}
											action={action}
											onClick={handleLogout}
											sx={{ my: 5 }}
										/>
									))}
								</List>
							</Stack>
						</Stack>
					</StyledDrawer>
					<StyledButtonSidebar open={open} desktop={isDesktop}>
						<span className="Sidebar-Button" onClick={() => setOpen(!open)}>
							{open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
						</span>
					</StyledButtonSidebar>
				</Box>
			</Box>
		</Fragment>
	);
};

export default Sidebar;
